import { TenantMemberDisplayData } from '@kudos/http-client'
import { useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { useMemo, useRef } from 'react'

export type SearchResults = {
  tenantMembers: TenantMemberDisplayData[]
}

export type TenantMemberSearchFn = (params: { search?: string }) => Promise<SearchResults>

/**
 * Debounced tenant-member search for autocomplete fields.
 *
 * Calls `searchFn` via React Query when `search` is non-empty and removes any
 * IDs in `selectedMembersIds` from the returned matches so already-selected
 * members stay out of the dropdown (avoids duplicate selection while typing).
 */
const useSearchTenantMembers = (
  /** Current autocomplete input; query runs only when this is non-empty. */
  search: string,
  /** Member IDs already chosen in the form; excluded from `searchedTenantMembers`. */
  selectedMembersIds: string[] = [],
  /** Injected search API (e.g. report sharing vs admin member list). */
  searchFn: TenantMemberSearchFn,
) => {
  const lastMatches = useRef<SearchResults | null>(null)

  const {
    data: matches,
    isSuccess,
    isFetching,
  } = useQuery({
    queryKey: ['searchTenantMembers', search],
    queryFn: async () => {
      const result = await searchFn({ search })
      return result
    },
    placeholderData: lastMatches.current || undefined,
    enabled: !isEmpty(search),
  })

  if (isSuccess) {
    lastMatches.current = matches
  }

  const searchedTenantMembers = useMemo(() => {
    if (isEmpty(search) || !matches) {
      return []
    }
    return (
      matches.tenantMembers.filter(match => !selectedMembersIds.find(id => id === match.id)) || []
    )
  }, [matches, search, selectedMembersIds])

  return {
    searchedTenantMembers,
    isFetching,
  }
}

export default useSearchTenantMembers
